import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';
import { normalizePath } from '../lib/format';
import { useElementSize } from '../lib/hooks';
import { VIEWPORT_WIDTHS, type Comment, type DomAnchor, type Item, type Person, type ViewInfo, type Viewport } from '../lib/types';
import { useAuth } from '../state/auth';
import { Icon } from '../components/Icon';
import { useToast } from '../components/toast';
import { Composer } from './Composer';
import { composerPosition } from './geometry';
import type { CommentDraft } from './CommentInput';
import type { NewComment } from './useComments';

interface PageInfo {
  url: string;
  path: string;
  title: string;
  viewportWidth?: number;
  viewportHeight?: number;
}

interface Draft {
  anchor: DomAnchor;
  x: number;
  y: number;
}

interface Props {
  item: Item;
  projectId: string;
  people: Person[];
  comments: Comment[];
  selectedId: string | null;
  highlightId: string | null;
  mode: 'browse' | 'comment';
  viewport: Viewport;
  onViewportChange: (viewport: Viewport) => void;
  showResolved: boolean;
  canComment: boolean;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
  onCreate: (input: NewComment) => Promise<Comment>;
}

/** Page details come from the reviewed site's frame, so treat them as untrusted input. */
function cleanPage(raw: unknown): PageInfo | null {
  if (!raw || typeof raw !== 'object') return null;
  const page = raw as Record<string, unknown>;
  const url = typeof page.url === 'string' && /^https?:\/\//i.test(page.url) ? page.url.slice(0, 2000) : null;
  if (!url) return null;
  return { url, path: typeof page.path === 'string' ? page.path.slice(0, 2000) : '/', title: typeof page.title === 'string' ? page.title.slice(0, 300) : '' };
}

/** Builds a matcher for the origins the review proxy serves pages from. */
function proxyOriginMatcher(proxyUrl: string | undefined) {
  if (!proxyUrl) return () => false;
  try {
    if (!proxyUrl.includes('//*.')) {
      const exact = new URL(proxyUrl).origin;
      return (origin: string) => origin === exact;
    }
    // Let URL normalise case and default ports, then turn the placeholder label back into a pattern.
    const [before, after] = new URL(proxyUrl.replace('//*.', '//mk-any-label.')).origin.split('mk-any-label.');
    const escape = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`^${escape(before)}[a-z0-9][a-z0-9-]*\\.${escape(after)}$`);
    return (origin: string) => pattern.test(origin);
  } catch {
    return () => false;
  }
}

const hostOfUrl = (url: string | null | undefined) => {
  try {
    return url ? new URL(url).host.toLowerCase() : null;
  } catch {
    return null;
  }
};

/**
 * Shows a website through the review proxy and talks to the review client
 * running inside it. The page lives on another origin, so everything crosses
 * the frame boundary as postMessage: pins go in, clicks and scroll positions
 * come out.
 */
export function WebsiteCanvas({ item, projectId, people, comments, selectedId, highlightId, mode, viewport, onViewportChange, showResolved, canComment, onSelect, onHover, onCreate }: Props) {
  const { meta } = useAuth();
  const toast = useToast();
  const [src, setSrc] = useState<string | null>(null);
  const [frameKey, setFrameKey] = useState(0);
  const [page, setPage] = useState<PageInfo | null>(null);
  const [ready, setReady] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [noLayer, setNoLayer] = useState(false);
  const [fatal, setFatal] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [address, setAddress] = useState(item.url || '');
  const [stageRef, stage] = useElementSize<HTMLDivElement>();
  const frame = useRef<HTMLIFrameElement>(null);
  const peerOrigin = useRef<string | null>(null);
  const pendingFocus = useRef<string | null>(null);
  const focusAfterPins = useRef<string | null>(null);
  const focusedFor = useRef<string | null>(null);
  const clickedPin = useRef<string | null>(null);
  const hasFrame = useRef(false);
  const pingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isProxyOrigin = useMemo(() => proxyOriginMatcher(meta?.proxyUrl), [meta?.proxyUrl]);

  const width = VIEWPORT_WIDTHS[viewport];
  const scale = stage.width > 0 ? Math.min(1, stage.width / width) : 1;
  const frameHeight = stage.height > 0 ? stage.height / scale : 800;
  const frameLeft = (stage.width - width * scale) / 2;

  const pins = useMemo(
    () =>
      comments
        .filter((c) => c.anchor.type === 'dom' && (c.anchor.viewport || 'desktop') === viewport && (showResolved || c.status !== 'resolved' || c.id === selectedId))
        .map((c) => {
          const a = c.anchor as DomAnchor;
          return { id: c.id, number: c.number, status: c.status, unread: c.unread, selector: a.selector, xPercent: a.xPercent, yPercent: a.yPercent, pageX: a.pageX, pageY: a.pageY, pagePath: a.pagePath || '/', pageHost: hostOfUrl(a.pageUrl) };
        }),
    [comments, viewport, showResolved, selectedId],
  );

  const send = useCallback((message: Record<string, unknown>) => {
    const target = frame.current?.contentWindow;
    if (target && peerOrigin.current) target.postMessage({ mk: 'app', ...message }, peerOrigin.current);
  }, []);

  const open = useCallback(
    async (url?: string) => {
      try {
        const view = await api.post<ViewInfo>(`/api/items/${item.id}/view`, url ? { url } : {});
        setFatal(null);
        setReady(false);
        setLoaded(false);
        setNoLayer(false);
        setDraft(null);
        if (pingTimer.current) clearTimeout(pingTimer.current);
        pingTimer.current = null;
        peerOrigin.current = null;
        hasFrame.current = true;
        setSrc(view.src);
        setFrameKey((key) => key + 1);
        return true;
      } catch (err) {
        // With a page already on screen a toast is enough; otherwise fill the stage.
        if (hasFrame.current) toast.error(errorMessage(err));
        else setFatal(errorMessage(err));
        return false;
      }
    },
    [item.id, toast],
  );

  useEffect(() => {
    void open();
  }, [open]);

  // Everything the message handler needs, without re-subscribing on each render.
  const live = useRef({ pins, selectedId, mode, onSelect, onHover, draft, scale, page });
  live.current = { pins, selectedId, mode, onSelect, onHover, draft, scale, page };

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!frame.current || event.source !== frame.current.contentWindow) return;
      const data = event.data;
      if (!data || data.mk !== 'client' || !isProxyOrigin(event.origin)) return;
      peerOrigin.current = event.origin;
      const now = live.current;

      if (data.type === 'ready') {
        const info = cleanPage(data.page);
        if (!info) return;
        if (pingTimer.current) clearTimeout(pingTimer.current);
        pingTimer.current = null;
        setPage(info);
        setAddress(info.url);
        setReady(true);
        setNoLayer(false);
        send({ type: 'init', pins: now.pins, activeId: now.selectedId, mode: now.mode, zoom: now.scale });
        if (pendingFocus.current) {
          send({ type: 'focus', id: pendingFocus.current });
          pendingFocus.current = null;
        }
      } else if (data.type === 'navigated') {
        const info = cleanPage(data.page);
        if (!info) return;
        setPage(info);
        setAddress(info.url);
        // Sites rewrite the URL for many reasons (scroll-spy hashes, filters). Only a real
        // change of page makes a half-written comment meaningless.
        if (!now.page || normalizePath(info.path) !== normalizePath(now.page.path)) setDraft(null);
      } else if (data.type === 'draft') {
        if (!data.anchor || typeof data.anchor.selector !== 'string' || typeof data.point?.x !== 'number' || typeof data.point?.y !== 'number') return;
        now.onSelect(null);
        setDraft({ anchor: data.anchor, x: data.point.x, y: data.point.y });
      } else if (data.type === 'track') {
        if (data.draft && typeof data.draft.x === 'number' && typeof data.draft.y === 'number') {
          setDraft((current) => current && { ...current, x: data.draft.x, y: data.draft.y });
        }
      } else if (data.type === 'pin-click' && typeof data.id === 'string') {
        // Remembered so the selection effect does not scroll to a pin that is already under the cursor.
        clickedPin.current = data.id === now.selectedId ? null : data.id;
        now.onSelect(data.id);
      } else if (data.type === 'pin-hover') {
        now.onHover(typeof data.id === 'string' ? data.id : null);
      } else if (data.type === 'escape') {
        if (now.draft) {
          setDraft(null);
          send({ type: 'draft-clear' });
        } else {
          now.onSelect(null);
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [isProxyOrigin, send]);

  // Keep the page's pins and mode in step with the app.
  useEffect(() => {
    if (!ready) return;
    send({ type: 'pins', pins, activeId: selectedId, mode, zoom: scale });
    // A pin on another screen size only exists in the page once that size's pins have been sent.
    if (focusAfterPins.current && pins.some((pin) => pin.id === focusAfterPins.current)) {
      send({ type: 'focus', id: focusAfterPins.current });
      focusAfterPins.current = null;
    }
  }, [ready, pins, selectedId, mode, scale, send]);

  useEffect(() => {
    if (ready) send({ type: 'highlight', id: highlightId });
  }, [ready, highlightId, send]);

  useEffect(() => {
    if (mode === 'browse' && live.current.draft) {
      setDraft(null);
      send({ type: 'draft-clear' });
    }
  }, [mode, send]);

  // Selecting a comment brings its pin into view: right device, right page.
  // Runs once per selection, as soon as both the comment and the first page are known,
  // so a link straight to a comment (?comment=) lands on the pin as well.
  const selectedComment = selectedId ? comments.find((c) => c.id === selectedId) : undefined;
  const pageKnown = page !== null;
  useEffect(() => {
    if (!selectedId) {
      focusedFor.current = null;
      return;
    }
    if (focusedFor.current === selectedId || !selectedComment || !ready || !page) return;
    focusedFor.current = selectedId;
    if (selectedComment.anchor.type !== 'dom') return;
    const anchor = selectedComment.anchor;
    const fromPin = clickedPin.current === selectedId;
    clickedPin.current = null;
    const wanted = anchor.viewport || 'desktop';
    if (normalizePath(anchor.pagePath) !== normalizePath(page.path) && anchor.pageUrl) {
      pendingFocus.current = selectedId;
      if (wanted !== viewport) onViewportChange(wanted);
      void open(anchor.pageUrl);
    } else if (wanted !== viewport) {
      focusAfterPins.current = selectedId;
      onViewportChange(wanted);
    } else if (!fromPin) {
      send({ type: 'focus', id: selectedId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per selection, when its inputs first exist
  }, [selectedId, selectedComment?.id, ready, pageKnown]);

  // The frame loaded but our client never reported in: an error page, or a
  // page the proxy could not rewrite. Show it anyway, with a clear warning.
  useEffect(() => {
    if (!loaded || ready) return;
    const timer = setTimeout(() => setNoLayer(true), 2500);
    return () => clearTimeout(timer);
  }, [loaded, ready]);

  // The frame can also move on by itself (a link to a PDF, the "leaves the site" notice).
  // After every load, ask the review layer to report in; silence means pins are gone.
  const onFrameLoad = () => {
    setLoaded(true);
    if (!live.current.page || !peerOrigin.current) return; // first load: the timer above covers it
    send({ type: 'ping' });
    if (pingTimer.current) clearTimeout(pingTimer.current);
    pingTimer.current = setTimeout(() => {
      pingTimer.current = null;
      setReady(false);
      setNoLayer(true);
      setDraft(null);
    }, 2000);
  };
  useEffect(
    () => () => {
      if (pingTimer.current) clearTimeout(pingTimer.current);
    },
    [],
  );

  const submit = async (input: CommentDraft) => {
    if (!draft) return;
    const comment = await onCreate({ ...input, itemId: item.id, anchor: { ...draft.anchor, viewport } });
    setDraft(null);
    send({ type: 'draft-clear' });
    clickedPin.current = comment.id; // already in view; do not scroll
    onSelect(comment.id);
  };

  const cancelDraft = () => {
    setDraft(null);
    send({ type: 'draft-clear' });
  };

  const navigate = async (event: FormEvent) => {
    event.preventDefault();
    const value = address.trim();
    if (!value) return;
    const ok = await open(/^[a-z]+:\/\//i.test(value) || value.startsWith('/') ? value : `https://${value}`);
    if (!ok) setAddress(page?.url || item.url || '');
  };

  // The composer follows the pin, and waits at the nearest edge while the pin is scrolled out
  // of view. It is never unmounted for that: what the reviewer typed has to survive a scroll.
  const clamp = (value: number, max: number) => Math.max(0, Math.min(max, value));
  const composerStyle = draft ? composerPosition(clamp(frameLeft + draft.x * scale, stage.width), clamp(draft.y * scale, stage.height), stage) : null;
  const draftKey = draft ? `${draft.anchor.selector}|${draft.anchor.xPercent}|${draft.anchor.yPercent}` : '';

  return (
    <div className="canvas">
      <div className="canvas-tools">
        <form className="url-field" onSubmit={navigate}>
          <Icon name="globe" />
          <input aria-label="Page address" value={address} onChange={(event) => setAddress(event.target.value)} spellCheck={false} />
        </form>
        <button type="button" className="icon-btn" aria-label="Reload page" title="Reload page" onClick={() => void open(page?.url)}>
          <Icon name="refresh" />
        </button>
        <a className="icon-btn" href={page?.url || item.url || '#'} target="_blank" rel="noreferrer" aria-label="Open the original page in a new tab" title="Open the original page in a new tab">
          <Icon name="external" />
        </a>
      </div>

      <div ref={stageRef} className={`canvas-stage ${mode === 'comment' ? 'is-commenting' : ''}`} data-state={fatal ? 'error' : ready ? 'ready' : noLayer ? 'no-layer' : 'loading'}>
        {src && (
          <div className="frame-holder" style={{ width, height: frameHeight, left: 0, transform: `translateX(${frameLeft}px) scale(${scale})` }}>
            <iframe
              key={frameKey}
              ref={frame}
              src={src}
              title={`${item.title} preview`}
              sandbox="allow-scripts allow-same-origin allow-forms"
              referrerPolicy="no-referrer"
              onLoad={onFrameLoad}
            />
          </div>
        )}

        {fatal && (
          <div className="canvas-overlay" role="alert">
            <Icon name="alert" className="icon-lg" />
            <h3>This website could not be opened</h3>
            <p className="muted">{fatal}</p>
            <button className="btn btn-secondary" onClick={() => void open()}>
              <Icon name="refresh" /> Try again
            </button>
          </div>
        )}

        {!fatal && !ready && !noLayer && (
          <div className="canvas-overlay" role="status">
            <span className="spinner" />
            <span className="muted">Loading the page</span>
          </div>
        )}

        {draft && composerStyle && <Composer title="New revision" style={composerStyle} focusKey={draftKey} projectId={projectId} people={people} onSubmit={submit} onCancel={cancelDraft} />}
      </div>

      <div className="canvas-foot">
        <span className="row">
          {noLayer && !ready ? (
            <span className="row" role="status">
              <Icon name="alert" size={14} /> Pins are unavailable on this page. Reload, or open another page of the site.
            </span>
          ) : mode === 'comment' && canComment ? (
            <span className="mode-hint">
              <Icon name="pin" size={12} /> Click anywhere on the page to add a revision
            </span>
          ) : (
            <span className="truncate">{page?.title || ' '}</span>
          )}
        </span>
        <span className="mono">
          {width} × {Math.round(frameHeight)} · {Math.round(scale * 100)}%
        </span>
      </div>
    </div>
  );
}
